import { Button, Checkbox, Divider, Modal, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import 'moment/locale/ja';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloseIconModal from '../../../../../assets/icons/close.png';
import PaperBlue from '../../../../../assets/icons/paper_blue.svg';
import { default as XIconModal } from '../../../../../assets/icons/x-icon-modal.svg';
import '../../../../../assets/styles/CM/CM0016.css';
import LoadingComponent from '../../../../../common/components/icons/LoadingComponent';
import ImageViewerModalComponent from '../../../../../common/components/image-viewer-modal/ImageViewerComponent';
import PdfViewerModalComponent from '../../../../../common/components/pdf-viewer-modal/PdfViewerModalComponent';
import COMMON, { PLAN_TYPE } from '../../../../../common/constants/COMMON';
import helpers from '../../../../../common/helpers/common';
import { RootState } from '../../../../../store';
import {
    DataTableConstructionBudgetBalance,
    DataTableItemizedBudgetBalance,
    DataTableSubmissionHistory,
    DataTableWorkCompleted,
} from '../../entity/Entity';
import { CM0016Handler } from '../handler/HandlerImpl';
import { CM0016TableHasSummary } from './CM0016TableHasSummary';
import CM0016Table from './Table';
import { resetCM0016 } from '../slice/Slice';
import { DataTableOverview } from '../../../CM006/entity/Entity';
import IconCircleCheck from '../../../../../assets/icons/akar-icons_circle-check-fill.svg';
import ApprovalFlow, {
    ItemApprove,
} from '../../../../../common/components/approval-flow/ApprovalFlow';

import { STATUS_CM_CHILD_TABLE } from '../../../../../common/constants/COMMON';
import { CheckBoxCollapseComponentCM007CM009 } from '../../../CM007/presenter/component/collapsed-table/ui/CheckBoxAll';
import YellowEyeIcon from '../../../../../assets/icons/yellow-eye-icon.svg'
import IconI from '../../../../../assets/icons/icon-i-red.svg'
import ApproveViewModal from '../../../../../common/components/modal-approve-view';

import EyeApprovedIcon from '../../../../../assets/icons/eye-approved.svg'
import EyeNotApprovedIcon from '../../../../../assets/icons/eye-not-approved.svg'
import EyeRejectIcon from '../../../../../assets/icons/eye-reject.svg'
import ApproveConfirmModal from '../../../../../common/components/modal-approve-confirm';

type Props = {
    handler: CM0016Handler;
    onSubmit?: () => void;
    onReject?: () => void;
    disableSubmitOrReject?: boolean;
    isCheckbox?: boolean
};

const CM0016UI = ({ handler, disableSubmitOrReject, isCheckbox = false, onSubmit, onReject }: Props) => {
    const isVisible = useSelector((state: RootState) => state.cm0016.isVisible);
    const loading = useSelector((state: RootState) => state.cm0016.loading);
    const dispatch = useDispatch();
    const documentId = useSelector((state: RootState) => state.cm0016.documentIdCM016);
    const documentOriginId = useSelector((state: RootState) => state.cm0016.documentOriginIdCM016);
    const constructionId = useSelector((state: RootState) => state.cm0016.constructionId);
    const submittedDate = useSelector((state: RootState) => state.cm0016.summitDateCm016);
    const isShowRetrieve = useSelector((state: RootState) => state.wrapper3453i.isRetrieve);
    const statusCM016 = useSelector((state: RootState) => state.cm0016.statusCM016);
    const isOpenImageViewer = useSelector(
        (state: RootState) => state.imageViewerModal.isOpenImageViewer,
    );
    const isDisabledCheckAll = handler.dataTableSubmissionHistory.some((element) => element?.status?.code === STATUS_CM_CHILD_TABLE.MONEY_WAITING_FOR_APPROVAL.CODE)

    const order = useSelector(
        (state: RootState) => state.auth?.authData?.order?.code
    );
    const isOrderFree = order === PLAN_TYPE.PLAN_FREE;

    const renderEye = (status: string) => {
        if (status === STATUS_CM_CHILD_TABLE.MONEY_APPROVED.CODE) return EyeApprovedIcon
        else if (status === STATUS_CM_CHILD_TABLE.MONEY_NOT_APPROVED.CODE) return EyeNotApprovedIcon
        else return IconI
    }

    useEffect(() => {
        Promise.all([
            handler.getListSubmittedIndirectCost(documentId, documentOriginId, constructionId),
            handler.getWorkflowIndirectApprovalProcess(documentId),
        ]);
        return () => {
            dispatch(resetCM0016());
        };
    }, []);

    const columnsableWorkCompleted: ColumnsType<DataTableOverview> = [
        {
            title: '契約金額',
            dataIndex: 'contractValue',
            align: 'center',
            width: '25%',
        },
        {
            title: '請求出来高',
            dataIndex: 'paymentValue',
            align: 'center',
            width: '25%',
        },
        {
            title: '請求出来高(％)',
            dataIndex: 'paymentValuePercent',
            align: 'center',
            width: '25%',
        },
        {
            title: '請求残金',
            dataIndex: 'invoiceBalance',
            align: 'center',
            width: '25%',
        },
    ];

    const columnsTableConstructionBudgetBalance: ColumnsType<DataTableConstructionBudgetBalance> = [
        {
            title: '予算金額',
            dataIndex: 'budgetAmount',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: '実費用',
            dataIndex: 'actualCost',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: '予算残高',
            dataIndex: 'budgetBalance',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: '予算消化率（％）',
            dataIndex: 'budgetRate',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${text}％`}</span>;
            },
        },
    ];

    const columnsTableItemizedBudgetBalance: ColumnsType<DataTableItemizedBudgetBalance> = [
        {
            title: '予算金額',
            dataIndex: 'budgetAmount',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: '実費用',
            dataIndex: 'actualCost',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: '予算残高',
            dataIndex: 'budgetBalance',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: '予算消化率（％）',
            dataIndex: 'budgetRate',
            align: 'center',
            width: '25%',
            render: (text) => {
                return <span>{`${text}％`}</span>;
            },
        },
    ];

    const columnsTableSubmissionHistory: ColumnsType<DataTableSubmissionHistory> = [
        {
            /*
            Hiển thị text: 項目追加
            Click button sẽ thêm 1 record, xem mục #8.3.10
            Điều kiện enable button: Khi status của record không ở status 承認待ち"				
            4/14/2023							
            */
            title: !isCheckbox || isOrderFree ? 'No' : <CheckBoxCollapseComponentCM007CM009
                onCheckAllChange={handler.onCheckAllChange}
                checkAll={handler.checkAll}
                isDisabled={!isDisabledCheckAll}
            />,
            key: 'No',
            align: 'center',
            width: 100,
            render: (_, record, index) => {
                if (!isCheckbox || isOrderFree) return <span className="cell-cm007-style white-space-nowrap"
                    style={{ width: '100%', textAlign: 'center' }} >
                    {index + 1}
                </span>
                const { MONEY_APPROVED, MONEY_REJECT, MONEY_RETRIEVE } = STATUS_CM_CHILD_TABLE

                return <span
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                    className="cell-cm007-style white-space-nowrap"
                    style={{ width: '100%', textAlign: 'center' }} >
                    {[
                        MONEY_REJECT.CODE,
                        MONEY_RETRIEVE.CODE,
                        MONEY_APPROVED.CODE].includes(record?.status?.code ?? '') ? (
                        <Checkbox
                            className="no-hover"
                            disabled={true}
                            style={{ marginRight: 8 }}
                            checked
                        />
                    ) : (
                        <Checkbox
                            onChange={(value: any) =>
                                handler.onCheckboxChange(
                                    record,
                                    value?.target?.checked ? true : false,
                                )
                            }
                            style={{ marginRight: 8 }}
                            checked={handler.isCheckbox(record)}
                        />
                    )}
                    {index + 1}
                </span>
            },
        },
        {
            title: '入力方法',
            dataIndex: 'inputMethod',
            align: 'center',
            render: (text) => {
                return <span>{text}</span>;
            },
        },
        {
            title: '対象期間',
            dataIndex: 'targetStage',
            align: 'center',
            render: (text) => {
                return <span>{text}</span>;
            },
        },
        {
            title: '支払日',
            dataIndex: 'paymentDate',
            align: 'center',
            render: (text) => {
                return <span>{text}</span>;
            },
        },
        {
            title: '支払予定金額',
            align: 'center',
            children: [
                {
                    title: '手入力',
                    dataIndex: 'manualInput',
                    align: 'center',
                    render: (text) => {
                        return <span>{`${helpers.formatCurrency(text)}円`}</span>;
                    },
                },
                {
                    title: '掛け係数',
                    dataIndex: 'multiplier',
                    align: 'center',
                    render: (text) => {
                        return <span>{`${helpers.formatCurrency(text)}`}</span>;
                    },
                },
                {
                    title: '金額(％集計)',
                    dataIndex: 'payAmount',
                    align: 'center',
                    render: (text) => {
                        return <span>{`${helpers.formatCurrency(text)}円`}</span>;
                    },
                },
            ],
        },
        {
            title: '実費用',
            dataIndex: 'actualCost',
            align: 'center',
            render: (text) => {
                return <span>{`${helpers.formatCurrency(text)}円`}</span>;
            },
        },
        {
            title: 'エビデンス',
            dataIndex: 'evidence',
            align: 'center',
            render: (text, raw) => {
                return (
                    text && (
                        <img
                            src={PaperBlue}
                            style={{ width: 24, height: 24, objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => handler.handleOpenViewer(raw)}
                        ></img>
                    )
                );
            },
        },
        {
            title: 'ステータス',
            key: 'ステータス',
            align: 'center',
            width: 150,
            render: (record) => {
                if (record?.status?.code === false) return <></>;
                const color = STATUS_CM_CHILD_TABLE.hasOwnProperty(
                    `${record?.status?.code?.toUpperCase()}`,
                )
                    ? {
                        color: STATUS_CM_CHILD_TABLE[`${record?.status?.code?.toUpperCase()}` as keyof typeof STATUS_CM_CHILD_TABLE]
                            .COLOR,
                    }
                    : {};
                return (
                    <div className="collapsed-table-row-style center-item" style={color}>
                        {`${record.status.name}` || ''}
                        <div style={{ marginLeft: 8 }} className='center-item'>
                            {
                                ([STATUS_CM_CHILD_TABLE.MONEY_APPROVED.CODE, STATUS_CM_CHILD_TABLE.MONEY_NOT_APPROVED.CODE, STATUS_CM_CHILD_TABLE.MONEY_REJECT.CODE].includes(record?.status?.code) && record?.note !== "")
                                    ?
                                    <div className='button-style' onClick={() => handler.viewApproveModal(record)} >
                                        <img alt='' src={renderEye(record?.status?.code)} />
                                    </div> : <></>
                            }
                        </div>
                    </div >
                );
            },
        },
    ];

    useEffect(() => {
        handler?.dataTableSubmissionHistory &&
            handler.handleCalculateSummary(handler.dataTableSubmissionHistory);
    }, [handler?.dataTableSubmissionHistory]);

    return (
        <Modal
            className="group-detail-modal"
            style={{ marginLeft: 300, marginTop: 40, marginBottom: 40 }}
            visible={isVisible}
            closable={false}
            maskClosable={false}
            width={'1520px'}
            centered={true}
            closeIcon={<img src={CloseIconModal} alt="close icon" />}
            footer={
                <div className="cm0016-footer">
                    {isShowRetrieve ? (
                        <Button
                            disabled={
                                loading ||
                                handler.checkedList.length === 0 || isOrderFree
                            }
                            size="large"
                            className="button-purple button-dsb"
                            style={{ marginLeft: 16, width: 120, letterSpacing: -1.5 }}
                            onClick={() => handler.retrieveDocument(documentId)}
                        >
                            取り下げ
                        </Button>
                    ) : null}
                    {onSubmit && onReject ? (
                        <>
                            {/* DONE */}
                            <ApproveConfirmModal onSubmit={onSubmit} />
                            <Button
                                className="button-purple cm047-btn-submit"
                                icon={<img alt="" src={IconCircleCheck} />}
                                size="large"
                                onClick={handler.showConfirmApproveModal}
                                disabled={
                                    disableSubmitOrReject ||
                                    !(
                                        handler.currentUserApproval &&
                                        helpers.checkApproveInWorkFlow(handler.currentUserApproval)
                                    )
                                }
                                style={{ marginLeft: 16 }}
                            >
                                承認
                            </Button>
                            <Button
                                size="large"
                                className="button-warning cm047-btn-reject"
                                onClick={onReject}
                                disabled={
                                    disableSubmitOrReject ||
                                    !(
                                        handler.currentUserApproval &&
                                        helpers.checkRejectInWorkFlow(handler.currentUserApproval)
                                    )
                                }
                            >
                                否認
                            </Button>
                        </>
                    ) : null}
                    <Button
                        disabled={loading}
                        size="large"
                        style={{
                            marginLeft: 16,
                            width: 118,
                            color: '#666',
                            fontWeight: 400,
                            fontSize: 14,
                        }}
                        onClick={handler.onCancel}
                    >
                        キャンセル
                    </Button>
                </div>
            }
        >
            <ImageViewerModalComponent
                imageURL={handler.pdfUrl}
                title={handler.pdfName}
            />
            <PdfViewerModalComponent
                pdfURL={handler.pdfUrl}
                title={handler.pdfName} />
            <Spin indicator={LoadingComponent} spinning={loading}>
                <div className="w-100-percent header-modal-containter" style={{ height: 50 }}>
                    <div className="header-modal-title-cm0016">間接工事費</div>
                    <div style={{ cursor: 'pointer' }} onClick={handler.onCancel}>
                        <img alt="" src={XIconModal} style={{ height: 18, width: 18 }} />
                    </div>
                </div>
                <div style={{ marginTop: 24, marginBottom: 8 }}>
                    <ApprovalFlow
                        isNotCheckingUser={true}
                        breadcrumb={[]}
                        listApprove={handler.workflow}
                        currentUserApproval={handler.currentUserApproval}
                    />
                </div>
                <div style={{ minHeight: 224, marginTop: 24 }} className="cm0016-content">
                    <div className="content-description">
                        <div className="description-col">
                            <div className="description-row">
                                <label>案件名</label>
                                <p>{handler.dataSummaryTitle?.project?.name ?? ''}</p>
                            </div>
                            <div className="description-row">
                                <label>項目名</label>
                                <p>{handler.dataSummaryTitle?.item ?? ''}</p>
                            </div>
                        </div>

                        <div className="description-col">
                            <div className="description-row">
                                <label>工事名</label>
                                <p>{handler.dataSummaryTitle?.construction?.name ?? ''}</p>
                            </div>
                            <div className="description-row">
                                <label>作成日</label>
                                <p>{submittedDate}</p>
                            </div>
                        </div>
                    </div>
                    <div className="content-table">
                        <CM0016Table
                            data={handler.dataReceivedMoney}
                            columns={columnsableWorkCompleted}
                            title="工事出来高"
                        />
                        <CM0016Table
                            data={handler.constructionInformationCost}
                            columns={columnsTableConstructionBudgetBalance}
                            title="工事全体間接工事費予算残高"
                        />
                        <CM0016Table
                            data={handler.indirectCost}
                            columns={columnsTableItemizedBudgetBalance}
                            title="項目別予算残高"
                        />

                        <Divider style={{ padding: '0px 24px', margin: '32px 0px' }}></Divider>
                        <CM0016TableHasSummary
                            data={handler.dataTableSubmissionHistory}
                            columns={columnsTableSubmissionHistory}
                            title="提出履歴"
                            handler={handler}
                        />
                    </div>
                </div>
            </Spin>
            <ApproveViewModal isViewOnly={true} />

        </Modal>
    );
};

export default CM0016UI;
